import { set } from '@ember/object';
import Route from '@ember/routing/route';
import type Transition from '@ember/routing/transition';
import { type Registry as Services, service } from '@ember/service';
import { task, type TaskInstance } from 'ember-concurrency';
import type { Market, User } from 'frontend-utils/models';
import { HOTEL_COOKIE_NAME } from 'frontend-utils/utils/constants/constant-values';
import { timeout } from 'lumen/utils/waiters';

type Model = Market | undefined;

type Params = {
  id: string;
};

export default class MarketRoute extends Route {
  @service declare access: Services['access'];
  @service declare cookie: Services['cookie'];
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intercom: Services['intercom'];
  @service declare satismeter: Services['satismeter'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  @service declare usageAnalytics: Services['usage-analytics'];
  @service declare usersnap: Services['usersnap'];

  marketRequestTask = task(
    { restartable: true },
    async (params: Params): Promise<Model> => {
      set(this, 'hotelAndMarketService.loading', true);

      const prefetched = this.store.peekRecord<Market>('market', params.id);

      if (prefetched) {
        await timeout();

        // Need to set the market already since we use it in the featuretoggle service
        this.hotelAndMarketService.setMarket(prefetched);

        return prefetched;
      }

      try {
        const markets = await this.store.query<Market>(
          'market',
          { id: params.id },
          { backgroundReload: false },
        );

        const market = markets?.[0];

        // Need to set the market already since we use it in the featuretoggle service
        // @ts-expect-error: Incorrect type
        this.hotelAndMarketService.setMarket(market);

        return market;
      } catch {
        this.cookie.resetCookie(HOTEL_COOKIE_NAME);

        window.location.href = '/';
      }
    },
  );

  async afterModel(model: Model): Promise<void> {
    this.satismeter.sendTraits();

    if (model) {
      set(this, 'hotelAndMarketService.loading', false);

      // @ts-expect-error: Incorrect type
      this.cookie.setCookie(HOTEL_COOKIE_NAME, {
        id: model.id,
        type: 'market',
      });

      this.intercom.updateMarket();

      const { user } = this.hotelAndMarketService;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.usersnap.initialise(user);

      await this.initialiseFeatureToggleService(user, model);

      // Only get the feature toggles that are enabled.
      const featureToggles = this.featureToggle.getFeatureToggles() ?? {};
      const enabledFeatureToggles = Object.keys(featureToggles).filter(
        (key) => featureToggles[key] === true,
      );

      // Call `identify` after the feature toggle service is initialised, so we can pass the feature toggles as trait.
      this.usageAnalytics.identify(user.id!, {
        isStaffUser: this.access.isStaffUser,
        featureToggles: enabledFeatureToggles,
        marketId: model.id,
      });
    }
  }

  beforeModel(): void {
    this.hotelAndMarketService.clearStore();
  }

  deactivate(): void {
    void this.usersnap.terminate();
  }

  async initialiseFeatureToggleService(
    user: User,
    market: Market,
  ): Promise<void> {
    let marketId: string | number | null = market.id;

    if (market.id) {
      const marketIdNumber = parseInt(market.id, 10);

      if (marketIdNumber) {
        marketId = marketIdNumber;
      }
    }

    // @ts-expect-error: Incorrect type
    await this.featureToggle.initialise(user, {
      isStaffUser: this.access.isStaffUser,
      marketId: marketId ?? undefined,
    });
  }

  model(params: Params): TaskInstance<Model> {
    return this.marketRequestTask.perform(params);
  }

  redirect(model: Model, transition: Transition): void {
    // Redirect to a subroute as /market itself doesn't serve anything.
    // The accessible tabs are checked by the route selections function in the beforeModel hook.
    if (transition.targetName === 'market.index') {
      this.transition.redirectToEnabledTab();
    }
  }
}
